import {Component, EventEmitter, Input, Output} from '@angular/core';
import {NgIf} from '@angular/common';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatDividerModule} from '@angular/material/divider';

/**
 * MAP-990: wraps a single style control row (app-slider-input, app-select-input,
 * app-color-input, app-text-input — projected unchanged via ng-content) and adds
 * the new expand/collapse column to its right (AC1-5).
 *
 * Deliberately a thin wrapper rather than a modification of the 4 shared input
 * components: the projected control is squeezed into `.style-row-control`
 * (flex: 1 1 auto), which narrows its internal Bootstrap-style `.row`/`.col-*`
 * layout automatically (AC3) without touching those components' markup — so
 * e.g. MAP-852's hue slider can still be added above color-input's swatches
 * without fighting this wrapper.
 *
 * - `hasToggle=false` (default): the toggle column renders empty — used for
 *   dependent-style rows (always, AC5) and for core styles with no dependents
 *   (AC4).
 * - `hasToggle=true`: renders the icon-smap-action-expand/collapse toggle
 *   (same icon-font pattern as MaporiumAccordionItemComponent) — used for
 *   core styles that currently have at least one dependent.
 * - `divider`: the thin separator rule above the row, matching how regular
 *   metadata properties are already separated (AC7). Core-style rows pass
 *   `divider=true` (except the section's first row); dependent rows pass
 *   `divider=false` so there's no line between a core style and its
 *   dependents, or between consecutive dependents (AC8). `divider=false`
 *   only HIDES the rule — the row still lays out the same 4px/rule/4px band,
 *   so hiding a line never changes the vertical rhythm.
 */
@Component({
  selector: 'maporium-style-row',
  standalone: true,
  imports: [NgIf, MatTooltipModule, MatDividerModule],
  template: `
    <mat-divider class="style-row-divider" [class.is-hidden]="!divider"></mat-divider>
    <div class="style-row">
      <div class="style-row-control">
        <ng-content></ng-content>
      </div>
      <div class="style-row-toggle"
           [class.is-clickable]="hasToggle"
           [matTooltip]="hasToggle ? tooltip : ''"
           matTooltipPosition="left"
           (click)="onToggleClick()">
        <span *ngIf="hasToggle" [class]="expanded ? 'icon-smap-action-collapse' : 'icon-smap-action-expand'"></span>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      /* Match the divider color already used for regular property rows
         (meta-data-control.component.scss) so core-style dividers (AC7) look
         identical to the "regular properties" ones the ticket references. */
      ---mat-divider-color: #DFDFDF;
    }
    /* Spacing reference: a regular property row in
       meta-data-control.component.html — \`<div class="row … mb-1 example-box">\`
       ending in \`<mat-divider class="mt-1">\`. That is 0.25rem (4px) between the
       field frame and the rule below it, and 0.25rem (4px) between that rule
       and the next field frame. Reproduced here as a symmetric 4px margin on
       the rule itself, so every style row (core or dependent) sits in the same
       4px / rule / 4px band. */
    .style-row-divider {
      margin-top: 4px;
      margin-bottom: 4px;
    }
    /* Dependent styles are not separated by lines (AC8) — hide the rule but
       keep the band, so the rhythm is identical with and without it. */
    .style-row-divider.is-hidden {
      border-top-color: transparent;
    }
    .style-row {
      display: flex;
      align-items: center;
    }
    .style-row-control {
      flex: 1 1 auto;
      min-width: 0;
    }
    /* The shared inputs each open with a bootstrap \`.row\` carrying its own
       \`mt-2\`/\`mb-2\` (app-slider-input, app-select-input, app-text-input;
       app-color-input has none). Stacked on top of the band above, that 8px
       made the gap below the rule three times the gap above it, and made
       one-line rows sit at a different rhythm than the multi-line property
       rows this spacing is copied from. The band owns the spacing now. */
    .style-row-control ::ng-deep .row {
      margin-top: 0 !important;
      margin-bottom: 0 !important;
    }
    .style-row-toggle {
      flex: 0 0 20px;
      width: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      color: #597080;
    }
    .style-row-toggle.is-clickable {
      cursor: pointer;
    }
  `],
})
export class StyleRowComponent {
  /** Whether this row is a collapsible core style (renders the toggle icon). */
  @Input() hasToggle = false;
  /** Current expand state of this row's dependents (icon + a11y state only; collapsing itself is driven by the caller). */
  @Input() expanded = false;
  /** Tooltip text for the toggle, e.g. "Show dependent color styles". */
  @Input() tooltip = '';
  /** Renders a thin divider above this row (AC7/AC8). */
  @Input() divider = true;
  @Output() toggleClick = new EventEmitter<void>();

  onToggleClick(): void {
    if (this.hasToggle) {
      this.toggleClick.emit();
    }
  }
}
